import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, DollarSign, TrendingUp, AlertCircle } from 'lucide-react';
import { productPriceAPI } from '../../services/garageApi';

const UpdatePriceModal = ({
  isOpen,
  onClose,
  productId,
  productType,
  currentCostPrice,
  currentSellingPrice,
  currentTaxRate,
  currentIsTaxInclusive,
  onPriceUpdated
}) => {
  const [costPrice, setCostPrice] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [isTaxInclusive, setIsTaxInclusive] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCostPrice(currentCostPrice || '');
      setSellingPrice(currentSellingPrice || '');
      setTaxRate(currentTaxRate || '0');
      setIsTaxInclusive(currentIsTaxInclusive || false);
      setReason('');
      setError('');
      setSuccess('');
    }
  }, [isOpen, currentCostPrice, currentSellingPrice, currentTaxRate, currentIsTaxInclusive]);

  const profitMargin = sellingPrice && costPrice ? (parseFloat(sellingPrice) - parseFloat(costPrice)) : 0;
  const marginPercent = costPrice && parseFloat(costPrice) > 0 ? ((profitMargin / parseFloat(costPrice)) * 100).toFixed(1) : '0.0';
  const finalPrice = isTaxInclusive
    ? parseFloat(sellingPrice || 0)
    : parseFloat(sellingPrice || 0) * (1 + parseFloat(taxRate || 0) / 100);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await productPriceAPI.updatePrice(productType, productId, {
        cost_price: parseFloat(costPrice),
        selling_price: parseFloat(sellingPrice),
        tax_rate: parseFloat(taxRate || 0),
        is_tax_inclusive: isTaxInclusive,
        reason: reason || null,
      });
      onClose();
      if (onPriceUpdated) onPriceUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update price');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Update Price</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">{success}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost Price</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costPrice}
                  onChange={(e) => setCostPrice(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={isTaxInclusive}
                    onChange={(e) => setIsTaxInclusive(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-10 h-5 rounded-full transition-colors ${isTaxInclusive ? 'bg-blue-500' : 'bg-gray-300'}`}>
                    <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform mt-0.5 ${isTaxInclusive ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                </div>
                <span className="text-sm text-gray-700">Tax Inclusive</span>
              </label>
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-blue-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Profit Margin:</span>
              <span className={`font-medium ${profitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${profitMargin.toFixed(2)} ({marginPercent}%)
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Final Price (incl. tax):</span>
              <span className="font-semibold text-blue-700">${finalPrice.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {isTaxInclusive ? 'Tax included in selling price' : `Selling Price + ${taxRate || 0}% tax`}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Change Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Supplier price increase, seasonal adjustment..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              {loading ? 'Updating...' : 'Update Price'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default UpdatePriceModal;
